/**
 * Client for POST /api/cognitory/v2/platform-admin/systems plan/execute create.
 */

import {
  COGNITORY_V2_PLATFORM_ADMIN_SYSTEMS_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export type PlatformAdministratorCreatePlan = {
  systemId: string;
  displayName: string;
  status: "provisioning";
  planTimestamp: string;
  writeSetHash: string;
};

export type PlatformAdministratorCreatedSystem = {
  systemId: string;
  displayName: string;
  status: "provisioning";
  enterable: false;
};

export type PlatformAdministratorCreatePlanResult =
  | {
      kind: "planned";
      requestId: string;
      plan: PlatformAdministratorCreatePlan;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "validation_failed"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

export type PlatformAdministratorCreateExecuteResult =
  | {
      kind: "created" | "already_exists";
      requestId: string;
      system: PlatformAdministratorCreatedSystem;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "validation_failed"
        | "create_conflict"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

async function postPlatformAdministratorCreate(
  body: Record<string, unknown>,
  input?: {
    apiBaseUrl?: string;
    bearerToken?: string | null;
    fetchImpl?: typeof fetch;
  },
): Promise<{ status: number; body: unknown; requestId: string | null }> {
  const fetchImpl = input?.fetchImpl ?? fetch;
  const token =
    input?.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { status: 401, body: null, requestId: null };
  }

  const base = input?.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  let response: Response;
  try {
    response = await fetchImpl(
      `${base}${COGNITORY_V2_PLATFORM_ADMIN_SYSTEMS_PATH}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        cache: "no-store",
        body: JSON.stringify(body),
      },
    );
  } catch {
    return { status: 503, body: null, requestId: null };
  }

  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }

  const requestId =
    parsed &&
    typeof parsed === "object" &&
    typeof (parsed as { requestId?: unknown }).requestId === "string"
      ? (parsed as { requestId: string }).requestId
      : null;

  return { status: response.status, body: parsed, requestId };
}

function isCreatePlan(
  value: unknown,
): value is PlatformAdministratorCreatePlan {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.systemId === "string" &&
    record.systemId.length > 0 &&
    typeof record.displayName === "string" &&
    record.displayName.length > 0 &&
    record.status === "provisioning" &&
    typeof record.planTimestamp === "string" &&
    record.planTimestamp.length > 0 &&
    typeof record.writeSetHash === "string" &&
    record.writeSetHash.length > 0
  );
}

function isCreatedSystem(
  value: unknown,
): value is PlatformAdministratorCreatedSystem {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.systemId === "string" &&
    record.systemId.length > 0 &&
    typeof record.displayName === "string" &&
    record.displayName.length > 0 &&
    record.status === "provisioning" &&
    record.enterable === false
  );
}

export async function planPlatformAdministratorSystemCreate(input: {
  systemId: string;
  displayName: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<PlatformAdministratorCreatePlanResult> {
  const { status, body, requestId } = await postPlatformAdministratorCreate(
    {
      phase: "plan",
      systemId: input.systemId,
      displayName: input.displayName,
    },
    input,
  );

  if (status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (status === 400) {
    return { kind: "validation_failed", requestId };
  }
  if (status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (status !== 200 || !body || typeof body !== "object") {
    return { kind: "unable_to_verify", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision !== "create_planned") {
    return { kind: "unable_to_verify", requestId };
  }

  const plan = (body as { plan?: unknown }).plan;
  if (!isCreatePlan(plan)) {
    return { kind: "unable_to_verify", requestId };
  }

  return {
    kind: "planned",
    requestId: requestId ?? "unknown",
    plan,
  };
}

export async function executePlatformAdministratorSystemCreate(input: {
  systemId: string;
  displayName: string;
  planTimestamp: string;
  writeSetHash: string;
  confirmCreate: boolean;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<PlatformAdministratorCreateExecuteResult> {
  const { status, body, requestId } = await postPlatformAdministratorCreate(
    {
      phase: "execute",
      systemId: input.systemId,
      displayName: input.displayName,
      planTimestamp: input.planTimestamp,
      writeSetHash: input.writeSetHash,
      confirmCreate: input.confirmCreate,
    },
    input,
  );

  if (status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (status === 400) {
    return { kind: "validation_failed", requestId };
  }
  if (status === 409) {
    return { kind: "create_conflict", requestId };
  }
  if (status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (status !== 200 || !body || typeof body !== "object") {
    return { kind: "unable_to_verify", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision !== "system_created" && decision !== "system_already_exists") {
    return { kind: "unable_to_verify", requestId };
  }

  const system = (body as { system?: unknown }).system;
  if (!isCreatedSystem(system)) {
    return { kind: "unable_to_verify", requestId };
  }

  return {
    kind: decision === "system_created" ? "created" : "already_exists",
    requestId: requestId ?? "unknown",
    system,
  };
}
